import { LightningElement, api, wire } from "lwc";
import { logger } from "@devforce/trailhead-client-logger";

import { isFocusable } from "common/a11y";
import { Popover, PopoverState, PopoverPlacement } from "common/popover";
import { assignedElements } from "common/slot";
import { hasFocusableChildren } from "common/a11y";
import { EffectAdapter } from "common/effectAdapter";
import { BooleanAttr, attrToBool } from "common/helpers";

let uid = 0;

export default class extends LightningElement {
  @api placement: PopoverPlacement = "top";
  @api disabled: BooleanAttr = false;

  @wire(EffectAdapter, { disabled: "$disabled" })
  private onEffect() {
    if (!this.popover) return;

    // Hide the popover
    if (attrToBool(this.disabled)) {
      this.popover.disable();
    } else {
      this.popover.enable();
    }
  }

  private instanceId = `_tooltip-${uid++}`;

  private popover: Popover = null!;
  private isHidden: boolean = true;

  renderedCallback() {
    try {
      this.create();
    } catch (error) {
      if (error instanceof Error) {
        logger.error(error.message);
      }
    }
  }

  private create() {
    if (this.popover || attrToBool(this.disabled)) {
      return;
    }

    // Get trigger element
    const triggerSlot = this.template.querySelector(
      `slot[name="trigger"]`
    ) as HTMLSlotElement;

    const trigger = assignedElements(triggerSlot)[0] as HTMLElement;

    if (!trigger) {
      logger.warn("No tooltip trigger element found.");
      return;
    }

    // Get tooltip element
    const tooltipSlot = this.template.querySelector(
      `slot[name="tooltip"]`
    ) as HTMLSlotElement;
    const tooltip = assignedElements(tooltipSlot)[0] as HTMLElement;

    if (!tooltip) {
      logger.warn("No tooltip element found.");
      return;
    }

    // Set ARIA attributes
    if(!trigger.getAttribute('aria-label')) {
      trigger.setAttribute("aria-describedby", this.instanceId);
    }
    
    tooltip.setAttribute("id", this.instanceId);
    this.removeAttribute("title");

    // Ensure trigger is focusable
    if (!isFocusable(trigger) && !hasFocusableChildren(trigger)) {
      trigger.setAttribute("tabindex", "0");
    }

    const popover = this.template.querySelector(".tooltip");

    if (popover) {
      // Instantiate Popover
      this.popover = new Popover(trigger, popover, {
        hideDelay: 350,
        placement: this.placement,
        onChange: (state) => this.handleChange(state),
      });
    }
  }

  private handleChange(nextState: PopoverState) {
    this.isHidden = !nextState.isOpen;
  }
}
